import type { AnimalTypeLabel } from '@/services/animal-management';
import {
  type AnimalPayload,
  type CreateAnimalInput,
  getPossibleDuplicates,
  type PossibleDuplicate,
} from '@/services/animal-management/api';
import { maxFieldLength } from '@/services/animal-management/field-limits';

import {
  type AddAnimalFormValues,
  buildCreateAnimalInput,
  isAddFieldRequired,
  isAddFieldVisible,
} from './add-animal-schema';

/**
 * Fields that identify each animal, entered per row in Bulk Add. Everything
 * else on the Add Animal form is shared by the whole batch.
 */
export const BULK_ROW_FIELDS = [
  'nameTag',
  'name',
  'tag',
  'rfidTag',
  'registrationNumber',
  'birthDate',
] as const;

export type BulkRowField = (typeof BULK_ROW_FIELDS)[number];

export const isBulkRowField = (field: string): field is BulkRowField =>
  (BULK_ROW_FIELDS as readonly string[]).includes(field);

export type BulkAddRow = {
  key: string;
  values: Record<BulkRowField, string>;
  /** Set when the API flagged the row; it then needs a reason to save. */
  duplicates: PossibleDuplicate[] | null;
  reason: string;
  error: string | null;
};

let nextKey = 0;

export const emptyBulkRow = (
  values: Partial<Record<BulkRowField, string>> = {},
): BulkAddRow => ({
  key: `bulk-row-${++nextKey}`,
  values: {
    nameTag: '',
    name: '',
    tag: '',
    rfidTag: '',
    registrationNumber: '',
    birthDate: '',
    ...values,
  },
  duplicates: null,
  reason: '',
  error: null,
});

export const bulkRowColumns = (type: AnimalTypeLabel | undefined) =>
  BULK_ROW_FIELDS.filter((field) => isAddFieldVisible(type, field));

export const isBlankRow = (row: BulkAddRow) =>
  BULK_ROW_FIELDS.every((field) => !row.values[field].trim());

export const missingRowFields = (
  type: AnimalTypeLabel | undefined,
  row: BulkAddRow,
) =>
  bulkRowColumns(type).filter(
    (field) => isAddFieldRequired(type, field) && !row.values[field].trim(),
  );

/** yyyy-MM-dd or M/D/YYYY to a local-midnight Date, like the date picker. */
export const parseRowDate = (value: string): Date | undefined => {
  const text = value.trim();
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  const [year, month, day] = iso
    ? [iso[1], iso[2], iso[3]]
    : us
      ? [us[3], us[1], us[2]]
      : [];
  if (!year) return undefined;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.getMonth() === Number(month) - 1 && date.getDate() === Number(day)
    ? date
    : undefined;
};

export type BulkRowProblem =
  | { kind: 'missing'; fields: BulkRowField[] }
  | { kind: 'tooLong'; field: BulkRowField; max: number }
  | { kind: 'birthDate' }
  | { kind: 'reason' };

/** The first thing stopping a filled-in row from being sent, if any. */
export const rowProblem = (
  type: AnimalTypeLabel | undefined,
  row: BulkAddRow,
): BulkRowProblem | null => {
  const missing = missingRowFields(type, row);
  if (missing.length) return { kind: 'missing', fields: missing };
  for (const field of BULK_ROW_FIELDS) {
    if (field === 'birthDate' || !isAddFieldVisible(type, field)) continue;
    const max = maxFieldLength(type, field);
    if (max !== undefined && row.values[field].trim().length > max)
      return { kind: 'tooLong', field, max };
  }
  if (
    isAddFieldVisible(type, 'birthDate') &&
    row.values.birthDate.trim() &&
    !parseRowDate(row.values.birthDate)
  )
    return { kind: 'birthDate' };
  if (row.duplicates && !row.reason.trim()) return { kind: 'reason' };
  return null;
};

export const buildBulkCreateInputs = (
  shared: AddAnimalFormValues,
  rows: BulkAddRow[],
): CreateAnimalInput[] =>
  rows.map((row) => ({
    ...buildCreateAnimalInput({
      ...shared,
      nameTag: row.values.nameTag,
      name: row.values.name,
      tag: row.values.tag,
      rfidTag: row.values.rfidTag,
      registrationNumber: row.values.registrationNumber,
      birthDate: parseRowDate(row.values.birthDate),
    }),
    duplicateOverrideReason:
      row.duplicates && row.reason.trim() ? row.reason.trim() : null,
  }));

const payloadError = (payload: AnimalPayload) =>
  Object.values(payload.errors ?? {})[0] ??
  payload.message ??
  'Could not add this animal.';

/**
 * Drops created rows and marks the rest: possible duplicates wait for a
 * reason, anything else shows the server's error.
 */
export const applyBulkResults = (
  rows: BulkAddRow[],
  results: AnimalPayload[],
): { remaining: BulkAddRow[]; created: number } => {
  const remaining: BulkAddRow[] = [];
  let created = 0;
  rows.forEach((row, index) => {
    const result = results[index];
    if (result?.success) {
      created++;
      return;
    }
    const duplicates = result ? getPossibleDuplicates(result) : [];
    remaining.push(
      duplicates.length > 0 && !row.reason.trim()
        ? { ...row, duplicates, error: null }
        : {
            ...row,
            error: result ? payloadError(result) : 'No response for this row.',
          },
    );
  });
  return { remaining, created };
};

const HEADER_ALIASES: Record<string, BulkRowField> = {
  'name (tag)': 'nameTag',
  nametag: 'nameTag',
  name: 'name',
  'pedigree name': 'name',
  tag: 'tag',
  rfid: 'rfidTag',
  'rfid tag': 'rfidTag',
  rfidtag: 'rfidTag',
  'metal tag': 'rfidTag',
  registration: 'registrationNumber',
  'registration number': 'registrationNumber',
  registrationnumber: 'registrationNumber',
  'birth date': 'birthDate',
  birthdate: 'birthDate',
  birth: 'birthDate',
  dob: 'birthDate',
};

export const BULK_TEMPLATE_HEADERS: Record<BulkRowField, string> = {
  nameTag: 'Name (Tag)',
  name: 'Name',
  tag: 'Tag',
  rfidTag: 'RFID Tag',
  registrationNumber: 'Registration Number',
  birthDate: 'Birth Date',
};

const splitLine = (line: string, delimiter: string) => {
  const cells: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (quoted) {
      if (char === '"' && line[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (char === '"') quoted = false;
      else cell += char;
    } else if (char === '"') quoted = true;
    else if (char === delimiter) {
      cells.push(cell);
      cell = '';
    } else cell += char;
  }
  cells.push(cell);
  return cells.map((c) => c.trim());
};

/**
 * Rows from a CSV or tab-separated file with a header row. Columns are matched
 * by header name; unknown columns are reported and ignored.
 */
export const parseBulkRows = (
  text: string,
): { rows: BulkAddRow[]; unknownHeaders: string[] } => {
  const lines = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter((line) => line.trim());
  if (!lines.length) return { rows: [], unknownHeaders: [] };

  const delimiter = lines[0].includes('\t') ? '\t' : ',';
  const headers = splitLine(lines[0], delimiter);
  const fields = headers.map(
    (header) => HEADER_ALIASES[header.toLowerCase()] ?? null,
  );
  const unknownHeaders = headers.filter((h, i) => h && !fields[i]);

  const rows = lines
    .slice(1)
    .map((line) => {
      const cells = splitLine(line, delimiter);
      const values: Partial<Record<BulkRowField, string>> = {};
      fields.forEach((field, i) => {
        if (field && cells[i]) values[field] = cells[i];
      });
      return emptyBulkRow(values);
    })
    .filter((row) => !isBlankRow(row));

  return { rows, unknownHeaders };
};

export const bulkTemplateCsv = (type: AnimalTypeLabel | undefined) =>
  bulkRowColumns(type)
    .map((field) => BULK_TEMPLATE_HEADERS[field])
    .join(',') + '\n';
